"use client";

import { ImageIcon } from "lucide-react";

const useCases = [
  {
    title: "Meetups & Event Series",
    description: "Reward recurring attendance with collectible digital assets. Build loyalty and keep your community coming back.",
    color: "from-blue-400 to-indigo-500",
  },
  {
    title: "Museums & Cultural Venues",
    description: "Drive foot traffic and promote education with interactive exhibit hunts and collectible rewards for visitors.",
    color: "from-amber-400 to-orange-500",
  },
  {
    title: "Conferences & Trade Shows",
    description: "Boost booth visits and session attendance with gamified experiences that keep attendees engaged all day.",
    color: "from-emerald-400 to-teal-500",
  },
  {
    title: "Festivals & Concerts",
    description: "Create unforgettable fan experiences with location-based challenges and exclusive digital collectibles.",
    color: "from-purple-400 to-pink-500",
  },
  {
    title: "Sporting Events",
    description: "Engage fans beyond the game with stadium-wide scavenger hunts, trivia challenges, and commemorative digital assets.",
    color: "from-red-400 to-rose-500",
  },
  {
    title: "Marketing & Brand Activations",
    description: "Turn any activation into an interactive adventure that captures attention, drives engagement, and collects leads.",
    color: "from-cyan-400 to-blue-500",
  },
  {
    title: "Educational Institutions",
    description: "Build digital certificate and badge programs that reward course completion, workshop attendance, and academic achievements with verifiable blockchain credentials.",
    color: "from-lime-400 to-emerald-500",
  },
];

function ImagePlaceholder({ color }: { color: string }) {
  return (
    <div className={`w-full h-full bg-gradient-to-br ${color} flex items-center justify-center`}>
      <ImageIcon className="w-10 h-10 text-white/50" />
    </div>
  );
}

export function WhosItFor() {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Who&apos;s It For?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From intimate meetups to massive festivals, our platform powers interactive experiences for any event type.
          </p>
        </div>

        {/* Bento-style layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto">
          {/* Large featured card - Meetups */}
          <div className="md:col-span-2 lg:col-span-2 group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="flex flex-col sm:flex-row h-full">
              <div className="sm:w-1/2 aspect-[4/3] sm:aspect-auto overflow-hidden">
                <ImagePlaceholder color={useCases[0].color} />
              </div>
              <div className="sm:w-1/2 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCases[0].title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCases[0].description}</p>
              </div>
            </div>
          </div>

          {/* Tall card - Museums */}
          <div className="lg:row-span-2 group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="aspect-[4/3] lg:aspect-[3/2] overflow-hidden">
              <ImagePlaceholder color={useCases[1].color} />
            </div>
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCases[1].title}</h3>
              <p className="text-gray-600 leading-relaxed">{useCases[1].description}</p>
            </div>
          </div>

          {/* Standard card - Conferences */}
          <div className="group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="aspect-video overflow-hidden">
              <ImagePlaceholder color={useCases[2].color} />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{useCases[2].title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{useCases[2].description}</p>
            </div>
          </div>

          {/* Standard card - Festivals */}
          <div className="group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="aspect-video overflow-hidden">
              <ImagePlaceholder color={useCases[3].color} />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{useCases[3].title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{useCases[3].description}</p>
            </div>
          </div>

          {/* Standard card - Sporting */}
          <div className="group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="aspect-video overflow-hidden">
              <ImagePlaceholder color={useCases[4].color} />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{useCases[4].title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{useCases[4].description}</p>
            </div>
          </div>

          {/* Wide card - Marketing */}
          <div className="md:col-span-2 group relative overflow-hidden rounded-3xl bg-white shadow-md hover:shadow-xl transition-all duration-300">
            <div className="flex flex-col sm:flex-row-reverse h-full">
              <div className="sm:w-1/2 aspect-[4/3] sm:aspect-auto overflow-hidden">
                <ImagePlaceholder color={useCases[5].color} />
              </div>
              <div className="sm:w-1/2 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCases[5].title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCases[5].description}</p>
              </div>
            </div>
          </div>

          {/* Wide card - Educational Institutions */}
          <div className="md:col-span-2 lg:col-span-3 group relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300">
            <div className="relative h-64 overflow-hidden">
              <ImagePlaceholder color={useCases[6].color} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl font-bold mb-2">{useCases[6].title}</h3>
                <p className="text-white/80 leading-relaxed max-w-2xl">{useCases[6].description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
